import { getTags } from './tagsSource';

/*
 * 标签匹配工具（着陆页四板块卡片 + 底部班级导航共用）。
 *
 * ============================================================
 * 本站标签的实际命名规则（2026-09-15 从数据库核实）
 * ============================================================
 *   主标签（班级）：name = AI-02班      slug = ai-02
 *                   name = AI-03班      slug = ai-03
 *   次标签（板块）：name = 在线课堂      slug = AI02-OnClass     parent_id = 1
 *                   name = 作业专区      slug = AI02-task        parent_id = 1
 *                   name = 经验交流      slug = AI02-Sharing     parent_id = 1
 *                   name = 班干会议      slug = AI02-Suggestion  parent_id = 1
 *
 * 关键点：**班级前缀加在 slug 上，名称保持通用短名**。
 * 所以「课程 → 在线课堂」这种中文名匹配永远命中不了，
 * 必须走 slug 关键字：班级 key（ai02）+ 板块 key（onclass）。
 *
 * ============================================================
 * 数据来源
 * ============================================================
 * 全部走 utils/tagsSource.js 自己请求 /api/tags 解析出的纯对象，
 * 【不再使用 app.store.all('tags')】——实测 store 里的标签常常为空或只有
 * 主标签，导致所有匹配失败、地址退化成 '/'。详见 tagsSource.js 顶部说明。
 */

// 常见分隔符：中英文横杠、下划线、冒号、斜杠、竖线、点、间隔号、空格
const SEPARATOR_RE = /^[\s\-_—–:：/|.·]+/;

// 归一化：小写 + 只保留 a-z 0-9
function norm(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/*
 * slug 是否可用。
 *
 * slug 为空 / 含空格或 / \ ? # % 时，拼出来的 /t/<slug> 在服务端
 * Utf8SlugDriver::fromSlug 里 firstOrFail 失败，直接抛
 * ModelNotFoundException（500）。这里先挡掉。
 */
export function validSlug(slug) {
  if (!slug) return false;
  return !/[\s/\\?#%]/.test(slug);
}

// 班级 key：'ai-02' → 'ai02'
export function classKeyOf(cls) {
  return norm(cls && cls.slug);
}

/*
 * 生成标签页地址。
 *
 * 【踩坑】不要用 app.route('tag', { slug: xxx })：
 * 实测会生成 /t/undefined?slug=AI02-task —— slug 没填进路由占位符，
 * 反而被当成 query 参数 → 404。这里直接手工拼 /t/<slug>，与服务端路由一致。
 */
export function tagUrl(tag) {
  const slug = tag && tag.slug;
  if (!validSlug(slug)) return '/';
  return '/t/' + encodeURIComponent(slug);
}

/*
 * 某个主标签下的全部次标签。
 *
 * 优先用 parentId；parentId 缺失时（API 没带 relationship）用 slug 前缀兜底：
 * 'ai02onclass' 以 'ai02' 开头 → 属于 ai-02 班。
 */
export function childTagsOf(cls) {
  if (!cls) return [];

  const clsKey = classKeyOf(cls);
  const all = getTags();

  return all.filter((t) => {
    if (t.id === cls.id) return false;
    if (t.parentId === cls.id) return true;

    if (clsKey) {
      const s = norm(t.slug);
      if (s && s.indexOf(clsKey) === 0 && s !== clsKey) return true;
    }

    return false;
  });
}

/*
 * 当前学员所属班级（主标签）。
 *
 * 优先级：
 *   1. 主标签（无 parentId）里 canStartDiscussion 为 true 的
 *   2. 主标签里有次标签的（权限没配好时的合理猜测）
 *   3. 第一个主标签
 * 保证任何情况下都能选出一个班，不会因为权限字段缺失而返回 null
 * （返回 null 会导致着陆页卡片地址退化成 '/'）。
 */
export function myClassTag() {
  const all = getTags();
  if (!all.length) return null;

  const primary = all.filter((t) => !t.parentId);
  if (!primary.length) return null;

  const mine = primary.find((t) => t.canStartDiscussion);
  if (mine) return mine;

  const withKids = primary.find((t) => childTagsOf(t).length > 0);
  if (withKids) return withKids;

  return primary[0];
}

/*
 * 去掉次标签名里的班级前缀，返回纯板块名。
 * 例：stripClassPrefix('AI-02班-在线课堂', 'AI-02班') → '在线课堂'
 */
export function stripClassPrefix(name, clsName) {
  const n = (name || '').trim();
  const c = (clsName || '').trim();

  if (c && n.indexOf(c) === 0) {
    const rest = n.slice(c.length).replace(SEPARATOR_RE, '');
    if (rest) return rest;
  }

  return n;
}

/*
 * slug 与「板块 key」的匹配得分（越小越优先，0 = 不匹配）。
 *
 * 期望命中：slug 'AI02-OnClass' → 'ai02onclass'
 *   clsKey='ai02', partKey='onclass' → 去掉 clsKey 后正好等于 partKey = 得分 2 ★
 */
function slugKeyScore(tag, partKey, clsKey) {
  const s = norm(tag.slug);
  if (!s || !partKey) return 0;

  if (s === partKey) return 1;

  if (clsKey && s.indexOf(clsKey) === 0) {
    const rest = s.slice(clsKey.length);
    if (rest === partKey) return 2;
    if (rest.indexOf(partKey) !== -1) return 3;
  }

  if (s.indexOf(partKey) !== -1) return 4;

  return 0;
}

/*
 * 次标签与板块的匹配得分（0 = 不匹配）。
 *
 * part.key   板块 slug 关键字（onclass / task / sharing / suggestion）—— 主依据
 * part.alt   后台标签真实名称（在线课堂 / 作业专区 / 经验交流 / 班干会议）
 * part.title 卡片显示文案（课程 / 作业 / 交流 / 班会）—— 名称层面兜底
 */
export function matchSubTag(tag, part, clsName, clsKey) {
  const slugScore = slugKeyScore(tag, norm(part.key), clsKey);
  if (slugScore > 0) return slugScore;

  const name = (tag.name || '').trim();
  if (!name) return 0;

  const candidates = [part.alt, part.title].filter(Boolean);

  for (let i = 0; i < candidates.length; i++) {
    const c = candidates[i];
    if (name === c) return 5 + i * 3;
    if (stripClassPrefix(name, clsName) === c) return 6 + i * 3;
    if (name.indexOf(c) !== -1) return 7 + i * 3;
  }

  return 0;
}

// 在本班次标签里挑匹配分最高的那个
function pickBestChild(cls, part) {
  const clsName = cls.name;
  const clsKey = classKeyOf(cls);
  let best = null;
  let bestScore = 0;

  childTagsOf(cls).forEach((t) => {
    if (!validSlug(t.slug)) return;

    const score = matchSubTag(t, part, clsName, clsKey);
    if (score > 0 && (bestScore === 0 || score < bestScore)) {
      best = t;
      bestScore = score;
    }
  });

  return best;
}

// 全站范围内找一个匹配该板块 key 的标签（未登录 / 未分班时兜底用）
function pickAnyPart(part) {
  const key = norm(part.key);
  const clsKey = classKeyOf(myClassTag());
  let best = null;
  let bestScore = 0;

  getTags().forEach((t) => {
    if (!validSlug(t.slug)) return;

    const score = slugKeyScore(t, key, clsKey);
    if (score > 0 && (bestScore === 0 || score < bestScore)) {
      best = t;
      bestScore = score;
    }
  });

  return best;
}

/*
 * 板块卡片该跳到哪个地址。
 *
 * 优先级：本班次标签 → 全站同名次标签 → 班级主标签页 → 首页。
 *
 * 绝不使用硬编码 slug：旧版兜底 /t/OnClass 在库里根本没有对应记录
 * （真实 slug 是 AI02-OnClass 这种带班级前缀的），点下去服务端
 * Utf8SlugDriver::fromSlug 的 firstOrFail() 会直接抛 500。
 */
export function resolvePartUrl(part) {
  const cls = myClassTag();

  if (cls) {
    const best = pickBestChild(cls, part);
    if (best) return tagUrl(best);
  }

  // 本班没匹配到（未分班 / 权限没配）：全站找一个该板块的次标签
  const any = pickAnyPart(part);
  if (any) return tagUrl(any);

  // 再退一步：班级主标签页（这个标签一定存在）
  if (cls && validSlug(cls.slug)) return tagUrl(cls);

  return '/';
}

// 诊断用：把当前解析状态挂到 window，方便手机/控制台查看
export function diag() {
  const cls = myClassTag();

  return {
    total: getTags().length,
    tags: getTags().map((t) => `${t.id}/${t.name}/${t.slug}/parent=${t.parentId}/csd=${t.canStartDiscussion}`),
    cls: cls ? `${cls.id}/${cls.name}/${cls.slug}` : null,
    subs: cls ? childTagsOf(cls).map((t) => `${t.id}/${t.name}/${t.slug}`) : [],
    urls: [
      { key: 'OnClass', url: resolvePartUrl({ key: 'OnClass', title: '课程', alt: '在线课堂' }) },
      { key: 'task', url: resolvePartUrl({ key: 'task', title: '作业', alt: '作业专区' }) },
      { key: 'Sharing', url: resolvePartUrl({ key: 'Sharing', title: '交流', alt: '经验交流' }) },
      { key: 'Suggestion', url: resolvePartUrl({ key: 'Suggestion', title: '班会', alt: '班干会议' }) },
    ],
  };
}
